import {
  FunctionComponent,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { modelsService, FilteredModel } from '../../services/modelsService';
import {
  useImageSettingsStore,
  ImageSize,
} from '../../store/imageSettingsStore';
import styles from '../../styles/components/image/ImageSettingsBar.module.css';

interface ImageSettingsBarProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRight?: number;
  anchorBottom?: number;
}

const ImageSettingsBar: FunctionComponent<ImageSettingsBarProps> = ({
  isOpen,
  onClose,
  anchorRight,
  anchorBottom,
}) => {
  const [availableModels, setAvailableModels] = useState<FilteredModel[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const modelsLoadedRef = useRef(false);

  // Store state
  const storeSelectedModelId = useImageSettingsStore(s => s.selectedModelId);
  const storeImageSize = useImageSettingsStore(s => s.imageSize);
  const storeSteps = useImageSettingsStore(s => s.steps);
  const storeQuantity = useImageSettingsStore(s => s.quantity);
  const storeSeed = useImageSettingsStore(s => s.seed);

  const setSelectedModelId = useImageSettingsStore(s => s.setSelectedModelId);
  const setImageSize = useImageSettingsStore(s => s.setImageSize);
  const setSteps = useImageSettingsStore(s => s.setSteps);
  const setQuantity = useImageSettingsStore(s => s.setQuantity);
  const setSeed = useImageSettingsStore(s => s.setSeed);

  // Local state for settings in the modal
  const [selectedModel, setSelectedModel] = useState('Select option');
  const [localSelectedModelId, setLocalSelectedModelId] = useState<
    string | null
  >(null);
  const [localImageSize, setLocalImageSize] = useState<ImageSize>('512x512');
  const [localSteps, setLocalSteps] = useState(25);
  const [localQuantity, setLocalQuantity] = useState(1);
  const [localSeed, setLocalSeed] = useState<number | 'random'>('random');
  const [seedInputValue, setSeedInputValue] = useState('');
  const [isRandomSeed, setIsRandomSeed] = useState(true);
  const [selectedMode, setSelectedMode] = useState<'standard' | 'draft'>(
    'standard'
  );

  // UI state
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [topGradientOpacity, setTopGradientOpacity] = useState(0);
  const [bottomGradientOpacity, setBottomGradientOpacity] = useState(1);
  const [modalPosition, setModalPosition] = useState({
    bottom: 120,
    maxHeight: 600,
  });

  // Refs
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const settingsBarRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Track original settings to compare with current values
  const [originalSettings, setOriginalSettings] = useState<{
    selectedModelId: string | null;
    imageSize: ImageSize;
    steps: number;
    quantity: number;
    seed: number | 'random';
  } | null>(null);

  // Image size options with aspect ratio frames
  const imageSizeOptions = [
    { value: '512x512' as ImageSize, label: 'Square', ratio: '1:1' },
    { value: '1024x768' as ImageSize, label: 'Landscape', ratio: '4:3' },
    { value: '768x1024' as ImageSize, label: 'Portrait', ratio: '4:5' },
  ];

  // Check if current settings differ from original settings
  const hasSettingsChanged = useCallback(() => {
    if (!originalSettings) return false;

    return (
      localSelectedModelId !== originalSettings.selectedModelId ||
      localImageSize !== originalSettings.imageSize ||
      localSteps !== originalSettings.steps ||
      localQuantity !== originalSettings.quantity ||
      localSeed !== originalSettings.seed
    );
  }, [
    originalSettings,
    localSelectedModelId,
    localImageSize,
    localSteps,
    localQuantity,
    localSeed,
  ]);

  // Handle apply button click
  const handleApply = useCallback(() => {
    if (!hasSettingsChanged()) return;

    // Update the store with local values
    if (localSelectedModelId !== null) {
      setSelectedModelId(localSelectedModelId);
    }
    setImageSize(localImageSize);
    setSteps(localSteps);
    setQuantity(localQuantity);
    setSeed(localSeed);

    // Update original settings to current values
    setOriginalSettings({
      selectedModelId: localSelectedModelId,
      imageSize: localImageSize,
      steps: localSteps,
      quantity: localQuantity,
      seed: localSeed,
    });

    // Close the settings bar
    onClose();
  }, [
    hasSettingsChanged,
    localSelectedModelId,
    localImageSize,
    localSteps,
    localQuantity,
    localSeed,
    setSelectedModelId,
    setImageSize,
    setSteps,
    setQuantity,
    setSeed,
    onClose,
  ]);
  // Handle scroll to update gradient opacity
  const handleScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const maxScroll = scrollHeight - clientHeight;

    const topOpacity = Math.min(scrollTop / 60, 1);
    const bottomOpacity = Math.min((maxScroll - scrollTop) / 60, 1);

    setTopGradientOpacity(topOpacity);
    setBottomGradientOpacity(bottomOpacity);
  }, []);

  // Update slider fill based on current value
  const updateSliderFill = useCallback(
    (slider: HTMLInputElement, value: number, min: number, max: number) => {
      const percentage = ((value - min) / (max - min)) * 100;
      slider.style.background = `linear-gradient(to right, var(--primary-base) 0%, var(--primary-base) ${percentage}%, var(--surface-surface_2) ${percentage}%, var(--surface-surface_2) 100%)`;
    },
    []
  );

  // Calculate optimal modal position to stay within viewport
  const calculateModalPosition = useCallback(() => {
    const viewportHeight = window.innerHeight;
    const modalHeight = 600;
    const minBottomMargin = 20;

    const availableHeight = viewportHeight - minBottomMargin;

    if (availableHeight < modalHeight) {
      const newMaxHeight = availableHeight - 40;
      const newBottom = minBottomMargin;
      setModalPosition({ bottom: newBottom, maxHeight: newMaxHeight });
    } else {
      setModalPosition({ bottom: 120, maxHeight: modalHeight });
    }
  }, []);

  // Handle seed input changes
  const handleSeedChange = useCallback((value: string) => {
    setSeedInputValue(value);
    if (value === '' || isNaN(Number(value))) {
      setLocalSeed('random');
    } else {
      setLocalSeed(Math.max(0, Math.round(Number(value))));
    }
  }, []);

  // Toggle between random and custom seed
  const handleSeedToggle = useCallback(
    (mode: 'random' | 'custom') => {
      if (mode === 'random') {
        setIsRandomSeed(true);
        setLocalSeed('random');
        setSeedInputValue('');
      } else {
        setIsRandomSeed(false);
        // If no seed value, generate a random one as placeholder
        if (seedInputValue === '' || localSeed === 'random') {
          const newSeed = Math.floor(Math.random() * 1000000000);
          setLocalSeed(newSeed);
          setSeedInputValue(newSeed.toString());
        } else {
          // Keep existing seed value
          setLocalSeed(
            typeof localSeed === 'number'
              ? localSeed
              : Math.floor(Math.random() * 1000000000)
          );
        }
      }
    },
    [seedInputValue, localSeed]
  );

  // Close dropdowns and settings bar when clicking outside
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      // Close dropdowns when clicking outside them
      if (
        modelDropdownRef.current &&
        !modelDropdownRef.current.contains(event.target as Node)
      ) {
        setIsModelDropdownOpen(false);
      }

      // Close settings bar when clicking outside it
      if (
        settingsBarRef.current &&
        !settingsBarRef.current.contains(event.target as Node)
      ) {
        if (hasSettingsChanged()) {
          handleApply();
        } else {
          onClose();
        }
      }
    },
    [onClose, hasSettingsChanged, handleApply]
  );

  // Handle escape key to close settings
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (hasSettingsChanged()) {
          handleApply();
        } else {
          onClose();
        }
      }
    },
    [onClose, hasSettingsChanged, handleApply]
  );

  // Load models effect
  useEffect(() => {
    const loadModels = async () => {
      if (modelsLoadedRef.current || isLoadingModels) {
        return;
      }

      setIsLoadingModels(true);
      try {
        // For image generation, we'll use a filtered set of models
        // This is a placeholder - in a real implementation, you'd filter for image models
        const models = await modelsService.getAvailableModels();

        // Filter for image generation models using modelType
        const imageModels = models.filter(
          model => model.modelType === 1 // Image generation models have modelType 1
        );

        // If no specific image models found, use fallback
        const finalModels =
          imageModels.length > 0
            ? imageModels
            : [
                { id: 'flux.1-dev', name: 'Flux.1 Dev', pricePer1M: 0 },
                {
                  id: 'stable-diffusion-xl',
                  name: 'Stable Diffusion XL',
                  pricePer1M: 0,
                },
              ];

        setAvailableModels(finalModels);
        modelsLoadedRef.current = true;

        if (finalModels.length > 0) {
          if (storeSelectedModelId) {
            const match = finalModels.find(m => m.id === storeSelectedModelId);
            if (match) {
              setSelectedModel(match.name);
            } else if (selectedModel === 'Select option') {
              setSelectedModel(finalModels[0].name);
              setLocalSelectedModelId(finalModels[0].id);
            }
          } else if (selectedModel === 'Select option') {
            setSelectedModel(finalModels[0].name);
            setLocalSelectedModelId(finalModels[0].id);
          }
        }
      } catch (error) {
        console.error('Failed to load models:', error);
      } finally {
        setIsLoadingModels(false);
      }
    };

    if (isOpen) {
      loadModels();
      calculateModalPosition();
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);

      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', handleScroll);
        handleScroll();
      }

      window.addEventListener('resize', calculateModalPosition);
    } else {
      modelsLoadedRef.current = false;
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', calculateModalPosition);
      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [
    isOpen,
    handleClickOutside,
    handleKeyDown,
    handleScroll,
    calculateModalPosition,
  ]);
  // Sync local selected model label with local state when id changes or models list updates
  useEffect(() => {
    if (!availableModels || availableModels.length === 0) return;
    if (!localSelectedModelId) return;
    const match = availableModels.find(m => m.id === localSelectedModelId);
    if (match && selectedModel !== match.name) {
      setSelectedModel(match.name);
    }
  }, [localSelectedModelId, availableModels]);

  // Calculate initial position when component mounts
  useEffect(() => {
    calculateModalPosition();
  }, [calculateModalPosition]);

  // Initialize slider fills only when modal opens
  useEffect(() => {
    if (!isOpen) return undefined;

    const timer = setTimeout(() => {
      const sliders = document.querySelectorAll('input[type="range"]');
      sliders.forEach(slider => {
        const input = slider as HTMLInputElement;
        const value = parseFloat(input.value);
        const min = parseFloat(input.min);
        const max = parseFloat(input.max);
        updateSliderFill(input, value, min, max);
      });
    }, 10);

    return () => clearTimeout(timer);
  }, [isOpen, updateSliderFill]);

  // Initialize local state when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalSelectedModelId(storeSelectedModelId);
      setLocalImageSize(storeImageSize);
      setLocalSteps(storeSteps);
      setLocalQuantity(storeQuantity);
      setLocalSeed(storeSeed);

      // Handle seed display
      if (storeSeed === 'random') {
        setIsRandomSeed(true);
        setSeedInputValue('');
      } else {
        setIsRandomSeed(false);
        setSeedInputValue(storeSeed.toString());
      }

      // Initialize original settings when opening
      setOriginalSettings({
        selectedModelId: storeSelectedModelId,
        imageSize: storeImageSize,
        steps: storeSteps,
        quantity: storeQuantity,
        seed: storeSeed,
      });
    }
  }, [
    isOpen,
    storeSelectedModelId,
    storeImageSize,
    storeSteps,
    storeQuantity,
    storeSeed,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      modelsLoadedRef.current = false;
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div
      id="image-settings-bar"
      ref={settingsBarRef}
      className={`${styles.settingsBar} ${isOpen ? styles.open : ''}`}
      style={{
        bottom: `${anchorBottom ?? modalPosition.bottom}px`,
        right: anchorRight !== undefined ? `${anchorRight}px` : undefined,
        maxHeight: `${modalPosition.maxHeight}px`,
      }}
    >
      <div className={styles.scrollContainer}>
        <div
          className={styles.gradientTop}
          style={{ opacity: topGradientOpacity }}
        ></div>
        <div ref={scrollContainerRef} className={styles.autoLayoutVertical}>
          {/* Model Selection */}
          <div className={styles.statedefaultAutoLayoutfal}>
            <div className={styles.sizesm}>
              <div className={styles.text}>Model</div>
            </div>
            <div
              ref={modelDropdownRef}
              className={`${styles.dropdownContainer} ${isModelDropdownOpen ? styles.open : ''}`}
            >
              <div
                className={styles.sizemdTypedefault}
                onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
              >
                <div className={styles.text1}>
                  <div className={styles.text2}>{selectedModel}</div>
                </div>
                <img
                  className={styles.rightIcon}
                  alt=""
                  src="/icons/chevron_down.svg"
                  style={{
                    transform: isModelDropdownOpen
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                  }}
                />
              </div>
              {isModelDropdownOpen && (
                <div className={styles.dropdownMenu}>
                  {availableModels.length > 0 ? (
                    availableModels.map(model => (
                      <div
                        key={model.id}
                        className={styles.dropdownItem}
                        onClick={() => {
                          setSelectedModel(model.name);
                          setLocalSelectedModelId(model.id);
                          setIsModelDropdownOpen(false);
                        }}
                      >
                        {model.name}
                      </div>
                    ))
                  ) : (
                    <div
                      className={styles.dropdownItem}
                      style={{ opacity: 0.6, cursor: 'default' }}
                    >
                      Loading models...
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mode Selection */}
          <div className={styles.autoLayoutVertical1}>
            <div className={styles.sizesm}>
              <div className={styles.text}>Mode</div>
            </div>
            <div className={styles.modeButtons}>
              <button
                className={`${styles.modeButton} ${selectedMode === 'standard' ? styles.active : ''}`}
                onClick={() => setSelectedMode('standard')}
              >
                Standard
              </button>
              <button
                className={`${styles.modeButton} ${selectedMode === 'draft' ? styles.active : ''}`}
                onClick={() => setSelectedMode('draft')}
              >
                Draft
              </button>
            </div>
          </div>

          {/* Image Size Selection */}
          <div className={styles.statedefaultAutoLayoutfal1}>
            <div className={styles.sizesm}>
              <div className={styles.text}>Image Size</div>
            </div>
            <div className={styles.imageSizeButtons}>
              {imageSizeOptions.map(option => (
                <button
                  key={option.value}
                  className={`${styles.imageSizeButton} ${localImageSize === option.value ? styles.active : ''}`}
                  onClick={() => setLocalImageSize(option.value)}
                >
                  <div className={styles.imageSizeContent}>
                    <div className={styles.imageSizeLabel}>{option.label}</div>
                    <div className={styles.imageSizeFrame}>
                      <div className={styles.aspectRatio}>{option.ratio}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Advanced Section Header */}
          <div className={styles.sectionHeader}>
            <div className={styles.text}>Advanced</div>
          </div>

          {/* Steps */}
          <div className={styles.autoLayoutVertical2}>
            <div className={styles.autoLayoutHorizontal}>
              <div className={styles.sizesm3}>
                <div className={styles.text}>Steps</div>
                <img className={styles.iconHelp} alt="" src="/icons/help.svg" />
              </div>
              <div className={styles.statedefault1}>
                <input
                  type="number"
                  min="1"
                  max="50"
                  step="1"
                  value={localSteps}
                  onChange={e => setLocalSteps(parseInt(e.target.value) || 1)}
                  className={styles.parameterInput}
                />
              </div>
            </div>
            <div className={styles.sliderContainer}>
              <input
                type="range"
                min="1"
                max="50"
                step="1"
                value={localSteps}
                onChange={e => {
                  const value = parseInt(e.target.value);
                  setLocalSteps(value);
                  updateSliderFill(e.target, value, 1, 50);
                }}
                className={styles.slider}
              />
            </div>
          </div>

          {/* Seed */}
          <div className={styles.autoLayoutVertical4}>
            <div className={styles.sizesm}>
              <div className={styles.text}>Seed</div>
            </div>
            <div className={styles.seedToggle}>
              <button
                className={`${styles.seedToggleButton} ${isRandomSeed ? styles.active : ''}`}
                onClick={() => handleSeedToggle('random')}
              >
                Random
              </button>
              <button
                className={`${styles.seedToggleButton} ${!isRandomSeed ? styles.active : ''}`}
                onClick={() => handleSeedToggle('custom')}
              >
                Custom
              </button>
            </div>
            {!isRandomSeed && (
              <div className={styles.seedInputContainer}>
                <input
                  type="text"
                  value={seedInputValue}
                  onChange={e => handleSeedChange(e.target.value)}
                  placeholder="2097630813"
                  className={styles.seedInput}
                />
                <button
                  className={styles.seedClearButton}
                  onClick={() => handleSeedChange('')}
                  type="button"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          <div className={styles.autoLayoutVerticalChild} />
        </div>
        <div
          className={styles.gradientBottom}
          style={{ opacity: bottomGradientOpacity }}
        ></div>
      </div>

      {/* Quantity - Fixed at bottom */}
      <div className={styles.quantitySection}>
        <div className={styles.autoLayoutHorizontal}>
          <div className={styles.sizesm3}>
            <div className={styles.text}>Quantity</div>
          </div>
          <div className={styles.statedefault1}>
            <input
              type="number"
              min="1"
              max="4"
              step="1"
              value={localQuantity}
              onChange={e => setLocalQuantity(parseInt(e.target.value) || 1)}
              className={styles.parameterInput}
            />
          </div>
        </div>
      </div>

      {/* Apply Button */}
      <div
        className={`${styles.sizemdTypeprimaryS} ${!hasSettingsChanged() ? styles.disabled : ''}`}
        onClick={handleApply}
        style={{ cursor: hasSettingsChanged() ? 'pointer' : 'not-allowed' }}
      >
        <div className={styles.autoLayoutHorizontal10}>
          <div className={styles.text28}>Apply</div>
        </div>
      </div>
    </div>
  );
};

export default ImageSettingsBar;
